import { Component } from 'react'
import * as cn from 'classnames'
import * as differenceBy from 'lodash/differenceBy'
import * as intersection from 'lodash/intersection'
import * as styles from 'components/features/insights/Insights2020/Insights2020.styl'
import { Insight as InsightInterface, PlacementType } from 'interfaces/insights'
import { insightShownActionCreator, setActiveInsightActionCreator } from 'actions/insightsActions'
import Insight2020 from 'components/features/insights/Insight2020/Insight2020'

export type InsightsOwnProps = {
  placement: PlacementType
  isFullWidth?: boolean
}

export type InsightsStateProps = {
  insights: InsightInterface[]
  isMobile?: boolean
}

export type InsightsDispatchProps = {
  // null in case of fake insights
  onInsightShown: typeof insightShownActionCreator | null
  setActiveInsight?: typeof setActiveInsightActionCreator
}

type InsightsProps = InsightsStateProps & InsightsDispatchProps & InsightsOwnProps

type InsightsState = {
  insights: InsightsProps['insights']
  placement: InsightsProps['placement']
  dismissed: InsightInterface | null
}

class Insights extends Component<InsightsProps, InsightsState> {
  static getDerivedStateFromProps (nextProps: InsightsProps, prevState: InsightsState) {
    const { insights: nextInsights, placement: nextPlacement } = nextProps
    const { insights: prevInsights, placement: prevPlacement } = prevState

    // detect dismissed insight
    // we use restInsights because nextInsights can containt totally new insights
    // and it can cause unnecessary dismiss animation
    const restInsights = intersection(nextInsights, prevInsights)
    const isDismissedByDismiss = restInsights.length !== 0 &&
      (prevInsights.length - restInsights.length) === 1
    const isDismissedByChangePlacement = prevPlacement !== nextPlacement &&
      prevInsights.length > 0 && nextInsights.length > 0
    const dismissed = isDismissedByDismiss || isDismissedByChangePlacement
      ? differenceBy(prevInsights, nextInsights, 'id')[0]
      : null

    return {
      dismissed,

      insights: nextProps.insights,
      placement: nextProps.placement,
    }
  }

  state: InsightsState = {
    insights: this.props.insights,
    placement: this.props.placement,
    dismissed: null,
  }

  private get hasNext () {
    return this.props.insights.length > 1
  }

  render () {
    const {
      insights,
    } = this.props

    if (!insights.length) {
      return null
    }

    return (
      <div className={ styles.insights }>
        { this.renderInsights() }
      </div>
    )
  }

  private renderInsights () {
    const { insights } = this.props
    const { dismissed } = this.state

    let insightsChildren = insights.map((insight, index) => {
      return this.renderInsight(insight, index === 0)
    })

    if (dismissed) {
      insightsChildren = [
        ...insightsChildren,
        this.renderInsight(dismissed, false, true),
      ]
    }

    return insightsChildren
  }

  private renderInsight (insight: InsightInterface, isActive: boolean, isDismissed = false) {
    const { onInsightShown } = this.props
    const onShown = isActive && onInsightShown ? onInsightShown : undefined

    const className = cn(styles.insightsItem, {
      [styles.insightsItemDismissed]: isDismissed,
    })

    return (
      <div
        key={ insight.id }
        className={ className }
      >
        <Insight2020
          insight={ insight }
          onShown={ onShown }
          hasNext={ this.hasNext }
          notTouchable={ !isActive }
        />
      </div>
    )
  }

}

export default Insights
